import type { DatabookPreviewOutlineGroup } from "@/lib/api/databook";

export const DATABOOK_SHEET_STALE_TIME = Infinity;
export const DATABOOK_SHEET_PAGE_SIZE = 500;

type DatabookSheetStorage = Pick<Storage, "getItem" | "setItem">;

function selectedSheetStorageKey(projectId: string): string {
  return `dilly.databookBuilder.lastSheet.${projectId}`;
}

export function loadLastDatabookSheet(
  projectId: string | undefined,
  storage?: DatabookSheetStorage,
): string | null {
  if (!projectId) return null;
  try {
    return (storage ?? window.localStorage).getItem(
      selectedSheetStorageKey(projectId),
    );
  } catch {
    return null;
  }
}

export function saveLastDatabookSheet(
  projectId: string | undefined,
  sheetName: string | null,
  storage?: DatabookSheetStorage,
): void {
  if (!projectId || !sheetName) return;
  try {
    (storage ?? window.localStorage).setItem(
      selectedSheetStorageKey(projectId),
      sheetName,
    );
  } catch {
    // Browsers may disable or limit local storage.
  }
}

export function databookSheetQueryKey(
  projectId: string | undefined,
  sheetName: string | null,
  rowOffset = 0,
  expandedOutlineGroups: readonly string[] = [],
  expandedColumnOutlineGroups: readonly string[] = [],
  previewRevision = 0,
): readonly [
  "databook-sheet",
  string | undefined,
  string | null,
  number,
  readonly string[],
  readonly string[],
  number,
] {
  return [
    "databook-sheet",
    projectId,
    sheetName,
    rowOffset,
    [...expandedOutlineGroups].sort(),
    [...expandedColumnOutlineGroups].sort(),
    previewRevision,
  ] as const;
}

export function previousDatabookSheetPage(rowOffset: number): number {
  return Math.max(0, rowOffset - DATABOOK_SHEET_PAGE_SIZE);
}

export function nextDatabookSheetPage(
  rowOffset: number,
  returnedRowCount: number,
): number {
  return rowOffset + returnedRowCount;
}

export function toggleDatabookOutlineGroup(
  expandedGroupIds: readonly string[],
  groupId: string,
  groups: readonly DatabookPreviewOutlineGroup[],
): string[] {
  const groupById = new Map(groups.map((group) => [group.id, group]));
  const group = groupById.get(groupId);
  if (!group) return [...expandedGroupIds];
  if (!expandedGroupIds.includes(groupId)) {
    return [...expandedGroupIds, groupId];
  }
  return expandedGroupIds.filter((expandedGroupId) => {
    const expandedGroup = groupById.get(expandedGroupId);
    return expandedGroup !== undefined
      && expandedGroup.id !== group.id
      && !(
        expandedGroup.level > group.level
        && expandedGroup.start_source_index >= group.start_source_index
        && expandedGroup.end_source_index <= group.end_source_index
      );
  });
}

export function selectedDatabookSheet(
  sheetNames: string[] | undefined,
  selectedSheetName: string | null,
): string | null {
  if (selectedSheetName && sheetNames?.includes(selectedSheetName)) {
    return selectedSheetName;
  }
  return sheetNames?.[0] ?? null;
}

export function activeDatabookSheetRowOffset(
  selectedSheetName: string | null,
  activeSheetName: string | null,
  rowOffset: number,
): number {
  return selectedSheetName === null || selectedSheetName === activeSheetName
    ? rowOffset
    : 0;
}
